import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { STOCK_MOVEMENT_REASONS, type ProductDto, type StockMovementReason } from '@gc/shared';

import { api, ApiError } from '@/lib/api';

interface StockAdjustDialogProps {
  product: ProductDto;
  onClose: () => void;
  onSaved: () => void;
}

/** Reasons a human adjusts a shelf count by hand. SALE and its reversal are
 *  written by the order path, never chosen here. */
const MANUAL_REASONS = STOCK_MOVEMENT_REASONS.filter(
  (reason) => reason !== 'SALE' && reason !== 'SALE_REVERSAL',
);

/**
 * SPEC §6.3 "Opening Balance (+ to adjust stock)".
 *
 * Submits a signed delta rather than a new absolute count: two staff members
 * counting the same shelf at the same time both get their change, instead of
 * the second silently overwriting the first.
 */
export function StockAdjustDialog({ product, onClose, onSaved }: StockAdjustDialogProps) {
  const { t } = useTranslation();

  const [delta, setDelta] = useState('1');
  const [reason, setReason] = useState<StockMovementReason>('MANUAL_ADJUSTMENT');
  const [note, setNote] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const parsed = Number(delta) || 0;
  const resulting = product.stockQuantity + parsed;

  const inputClass =
    'w-full rounded-control border border-subtle bg-input px-3 py-2 text-content outline-none focus:border-primary';

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/60 p-4">
      <form
        onSubmit={(event) => void handleSubmit(event)}
        className="w-full max-w-md rounded-card border border-subtle bg-raised p-5 shadow-2xl"
      >
        <h2 className="text-lg font-semibold text-content">{t('cafe.products.adjustStock')}</h2>
        <p className="mt-1 mb-4 text-sm text-content-secondary">
          {product.name} · {t('cafe.products.inStock', { count: product.stockQuantity })}
        </p>

        <label className="block">
          <span className="mb-1 block text-sm text-content-secondary">
            {t('cafe.products.delta')}
          </span>
          <input
            type="number"
            step="1"
            value={delta}
            onChange={(event) => setDelta(event.target.value)}
            required
            className={`${inputClass} tabular`}
          />
          <span className="mt-1 block text-xs text-content-muted">
            {t('cafe.products.deltaHint')}
          </span>
        </label>

        <label className="mt-3 block">
          <span className="mb-1 block text-sm text-content-secondary">
            {t('cafe.products.reason')}
          </span>
          <select
            value={reason}
            onChange={(event) => setReason(event.target.value as StockMovementReason)}
            className={inputClass}
          >
            {MANUAL_REASONS.map((value) => (
              <option key={value} value={value}>
                {t(`cafe.products.reasons.${value}`)}
              </option>
            ))}
          </select>
        </label>

        <label className="mt-3 block">
          <span className="mb-1 block text-sm text-content-secondary">
            {t('cafe.products.note')}
          </span>
          <input
            value={note}
            onChange={(event) => setNote(event.target.value)}
            className={inputClass}
          />
        </label>

        <p className={`mt-3 text-sm ${resulting < 0 ? 'text-danger' : 'text-content-secondary'}`}>
          {t('cafe.products.resultingStock', { count: resulting })}
        </p>

        {error ? (
          <p role="alert" className="mt-2 text-sm text-danger">
            {error}
          </p>
        ) : null}

        <div className="mt-5 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-full border border-subtle px-4 py-2 text-sm text-content-secondary hover:bg-hover"
          >
            {t('common.cancel')}
          </button>
          <button
            type="submit"
            disabled={submitting || parsed === 0 || resulting < 0}
            className="rounded-full bg-primary px-4 py-2 text-sm font-medium text-primary-fg hover:bg-primary-hover disabled:cursor-not-allowed disabled:opacity-50"
          >
            {t('common.save')}
          </button>
        </div>
      </form>
    </div>
  );

  async function handleSubmit(event: FormEvent): Promise<void> {
    event.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await api.post(`/products/${product.id}/stock`, {
        delta: parsed,
        reason,
        note: note.trim() || null,
      });
      onSaved();
      onClose();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : t('cafe.products.couldNotSave'));
    } finally {
      setSubmitting(false);
    }
  }
}
